/**
 * Capacités caméra natives exposées par le navigateur / PWA
 * (MediaStreamTrack.getCapabilities + ImageCapture).
 * Tout est fail-soft : iOS Safari n’a souvent pas de torch.
 */

export interface NativeCamCaps {
  torch: boolean;
  zoom: boolean;
  zoomMin: number;
  zoomMax: number;
  zoomStep: number;
  canFlip: boolean;
  focusModes: string[];
  exposureModes: string[];
  fillLightModes: string[];
}

export const EMPTY_CAPS: NativeCamCaps = {
  torch: false,
  zoom: false,
  zoomMin: 1,
  zoomMax: 1,
  zoomStep: 0.1,
  canFlip: true,
  focusModes: [],
  exposureModes: [],
  fillLightModes: [],
};

type AnyCaps = Record<string, any>;

function trackCaps(track: MediaStreamTrack | null | undefined): AnyCaps {
  if (!track?.getCapabilities) return {};
  try {
    return (track.getCapabilities() as AnyCaps) || {};
  } catch {
    return {};
  }
}

export function readNativeCaps(track: MediaStreamTrack | null | undefined): NativeCamCaps {
  const caps = trackCaps(track);
  const zoom = caps['zoom'];
  const hasZoom =
    zoom &&
    typeof zoom === 'object' &&
    Number(zoom.max) > Number(zoom.min);

  const facing = Array.isArray(caps['facingMode']) ? caps['facingMode'] : [];
  return {
    torch: caps['torch'] === true,
    zoom: !!hasZoom,
    zoomMin: hasZoom ? Number(zoom.min) : 1,
    zoomMax: hasZoom ? Number(zoom.max) : 1,
    zoomStep: hasZoom ? Number(zoom.step || 0.1) : 0.1,
    canFlip: facing.length !== 1 || facing.includes('environment') || facing.includes('user'),
    focusModes: Array.isArray(caps['focusMode']) ? caps['focusMode'].map(String) : [],
    exposureModes: Array.isArray(caps['exposureMode']) ? caps['exposureMode'].map(String) : [],
    fillLightModes: [],
  };
}

/** Complète fillLightModes via ImageCapture (Chrome Android). */
export async function enrichFillLightModes(
  track: MediaStreamTrack,
  base: NativeCamCaps,
): Promise<NativeCamCaps> {
  try {
    const IC = (globalThis as any).ImageCapture;
    if (!IC) return base;
    const ic = new IC(track);
    if (typeof ic.getPhotoCapabilities !== 'function') return base;
    const photoCaps = await ic.getPhotoCapabilities();
    const modes = photoCaps?.fillLightMode;
    if (Array.isArray(modes) && modes.length) {
      return { ...base, fillLightModes: modes.map(String), torch: base.torch || modes.includes('torch') };
    }
  } catch {
    /* ignore */
  }
  return base;
}

async function applyAdvanced(
  track: MediaStreamTrack,
  patch: Record<string, unknown>,
): Promise<boolean> {
  try {
    await track.applyConstraints({ advanced: [patch as MediaTrackConstraintSet] });
    return true;
  } catch {
    try {
      await track.applyConstraints(patch as MediaTrackConstraintSet);
      return true;
    } catch {
      return false;
    }
  }
}

/** Torche / flash continu — MediaTrackConstraint `torch` ou ImageCapture fillLightMode. */
export async function setTorch(track: MediaStreamTrack, on: boolean): Promise<boolean> {
  if (await applyAdvanced(track, { torch: on })) return true;

  try {
    const IC = (globalThis as any).ImageCapture;
    if (!IC) return false;
    const ic = new IC(track);
    if (typeof ic.setOptions === 'function') {
      await ic.setOptions({ fillLightMode: on ? 'torch' : 'off' });
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

export async function setZoom(track: MediaStreamTrack, zoom: number): Promise<boolean> {
  const caps = readNativeCaps(track);
  if (!caps.zoom) return false;
  const z = Math.max(caps.zoomMin, Math.min(caps.zoomMax, zoom));
  return applyAdvanced(track, { zoom: z });
}

/** Active l’autofocus continu + expo continue si dispo (scan documents). */
export async function applyScanDefaults(track: MediaStreamTrack): Promise<void> {
  const caps = readNativeCaps(track);
  if (caps.focusModes.includes('continuous')) {
    await applyAdvanced(track, { focusMode: 'continuous' });
  } else if (caps.focusModes.includes('single-shot')) {
    await applyAdvanced(track, { focusMode: 'single-shot' });
  }
  if (caps.exposureModes.includes('continuous')) {
    await applyAdvanced(track, { exposureMode: 'continuous' });
  }
}

export function currentZoom(track: MediaStreamTrack | null | undefined): number {
  try {
    const s = track?.getSettings?.() as AnyCaps | undefined;
    return Number(s?.['zoom']) || 1;
  } catch {
    return 1;
  }
}

export function videoTrackOf(stream: MediaStream | null | undefined): MediaStreamTrack | null {
  return stream?.getVideoTracks?.()[0] ?? null;
}

/** Contraintes getUserMedia optimisées scan (arrière, HD). */
export function buildScanConstraints(
  facing: 'environment' | 'user',
): MediaStreamConstraints {
  return {
    audio: false,
    video: {
      facingMode: { ideal: facing },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      // frameRate idéal pour preview fluide (non bloquant)
      frameRate: { ideal: 30, max: 60 },
    } as MediaTrackConstraints,
  };
}
